// The boundary for catalog JSON the generated client cannot type: workflow summaries (their
// schema name collides with the legacy workflow's) and the graph and requirements documents
// (plain JSON in the spec). The API serialises its own validated domain types, so these check
// the outline and refuse anything else instead of re-validating every field.
import { ApiError } from "../api/queries";
import type { WorkflowStats } from "../ratings/model";
import { type Graph, type Issue, type Requirement, TEMPLATE_STATUSES, type TemplateStatus, type WorkflowSummary } from "./types";

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null && !Array.isArray(x);

function malformed(what: string): never {
  throw new Error(`ocs-api sent a malformed ${what}`);
}

const optionalNumber = (x: unknown): number | null => (typeof x === "number" ? x : null);

export function parseWorkflow(x: unknown): WorkflowSummary {
  if (!isObject(x) || typeof x.id !== "string" || typeof x.name !== "string" || typeof x.task_type_id !== "string") {
    return malformed("workflow");
  }
  const status = TEMPLATE_STATUSES.find((s) => s === x.status) ?? malformed("workflow status");
  return {
    id: x.id,
    slug: String(x.slug),
    name: x.name,
    description: typeof x.description === "string" ? x.description : "",
    task_type_id: x.task_type_id,
    icon: typeof x.icon === "string" ? x.icon : null,
    status: status satisfies TemplateStatus,
    created_by: String(x.created_by),
    created_at: Number(x.created_at),
    updated_at: Number(x.updated_at),
    latest_version: optionalNumber(x.latest_version),
    draft_version: optionalNumber(x.draft_version),
    stats: parseStats(x.stats),
  };
}

function parseStats(x: unknown): WorkflowStats {
  if (!isObject(x) || typeof x.usage_count !== "number" || typeof x.rating_count !== "number") return malformed("workflow stats");
  return {
    usage_count: x.usage_count,
    rating_count: x.rating_count,
    review_count: Number(x.review_count),
    average_results: optionalNumber(x.average_results),
    average_cost: optionalNumber(x.average_cost),
    rating: optionalNumber(x.rating),
  };
}

export function parseWorkflows(x: unknown): WorkflowSummary[] {
  return Array.isArray(x) ? x.map(parseWorkflow) : malformed("workflow list");
}

export function parseGraph(x: unknown): Graph {
  if (!isObject(x) || typeof x.start !== "string" || !Array.isArray(x.nodes) || !Array.isArray(x.edges)) {
    return malformed("workflow graph");
  }
  return x as unknown as Graph;
}

export function parseRequirements(x: unknown): Requirement[] {
  return Array.isArray(x) ? (x as Requirement[]) : malformed("requirements list");
}

/** The validation issues a refused publish (422) carries, or none. */
export function refusedIssues(error: unknown): Issue[] {
  if (!(error instanceof ApiError) || error.status !== 422 || !isObject(error.body)) return [];
  const issues = error.body.issues;
  return Array.isArray(issues) ? (issues as Issue[]) : [];
}
